<template>
  <div
    v-if="showModal"
    class="slippage-confirm-modal"
    @click.self="cancel"
  >
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h2 class="modal-title">
            <font-awesome-icon :icon="faTriangleExclamation" class="warn-icon" />
            Confirm your price
          </h2>
          <div class="close-btn" @click="cancel">
            <img
              src="/icons/close.svg"
              alt="close"
              class="close-icon day-mode"
            />
            <img
              src="/icons/close-night.svg"
              alt="close"
              class="close-icon night-mode"
            />
          </div>
        </div>
      </div>

      <div class="body">
        <p class="summary-note">
          Part of this bet fills at a price that's significantly worse than the
          best price ({{ data.bestOddsDisplay }}). Are you sure you want to
          place this bet as is?
        </p>

        <div class="fills">
          <div class="fills-head">
            <span class="cell label">Amount</span>
            <span class="cell">Price</span>
          </div>
          <div
            v-for="(fill, index) in data.worseFills"
            :key="index"
            class="fills-row"
          >
            <span class="cell label">{{ money(fill.amount) }}</span>
            <span class="cell worse">{{ fill.oddsDisplay }}</span>
          </div>
          <div class="fills-row best">
            <span class="cell label">Best price</span>
            <span class="cell">{{ data.bestOddsDisplay }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <button class="cancel-btn" @click="cancel">Cancel</button>
        <button class="confirm-btn" @click="confirm">Place bet</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';

const showModal = ref(false);
const data = ref({
  bestOddsDisplay: '',
  worseFills: [],
  onConfirm: null,
  onCancel: null,
});

// Guards against the cancel callback firing twice (e.g. confirm closes the
// modal, then a stray backdrop click). Only one of confirm/cancel ever runs.
let resolved = false;

function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function show(slippageData) {
  data.value = {
    bestOddsDisplay: slippageData?.bestOddsDisplay ?? '',
    worseFills: slippageData?.worseFills ?? [],
    onConfirm: slippageData?.onConfirm ?? null,
    onCancel: slippageData?.onCancel ?? null,
  };
  resolved = false;
  showModal.value = true;
}

function confirm() {
  if (resolved) return;
  resolved = true;
  showModal.value = false;
  data.value.onConfirm?.();
}

function cancel() {
  if (resolved) return;
  resolved = true;
  showModal.value = false;
  data.value.onCancel?.();
}

defineExpose({
  show,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

.slippage-confirm-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: main.$black-202;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    padding: 0.5rem;
  }
}

.modal-content {
  background: main.$white_01;
  border-radius: 12px;
  box-shadow: 0 4px 20px main.$black-201;
  width: 100%;
  max-width: 420px;
  overflow: hidden;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    max-width: 100%;
    border-radius: 8px;
  }
}

.modal-header {
  background: main.$color-primary_02;
  padding: 1rem 1.5rem;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-title {
    color: main.$white;
    font-size: 1.25rem;
    font-weight: main.$font-weight-bold;
    margin: 0;
    font-family: 'Poppins Bold', Arial, sans-serif;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .warn-icon {
      font-size: 1.1rem;
    }
  }

  .close-btn {
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
      background: main.$black-103;
    }

    .close-icon {
      width: 20px;
      height: 20px;
    }
  }
}

.body {
  padding: 1.5rem;
  background: main.$white;
}

.summary-note {
  margin: 0 0 1.25rem;
  color: main.$black-203;
  font-size: 0.875rem;
  line-height: 1.4;
  font-family: 'Poppins Medium', Arial, sans-serif;
}

.fills {
  border: 1px solid main.$grey-402;
  border-radius: 8px;
  overflow: hidden;

  .fills-head,
  .fills-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid main.$grey-402;

    &:last-child {
      border-bottom: none;
    }

    .cell {
      font-size: 0.875rem;
      text-align: right;
      color: main.$black;
      font-weight: main.$font-weight-bold;
      font-family: 'Poppins Bold', Arial, sans-serif;

      &.label {
        text-align: left;
        color: main.$black-203;
        font-family: 'Poppins Medium', Arial, sans-serif;
      }

      &.worse {
        color: main.$red-300;
      }
    }
  }

  .fills-head {
    background: main.$white-200;

    .cell {
      font-size: 0.75rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: main.$black-203;
      font-family: 'Poppins Medium', Arial, sans-serif;
    }
  }

  .fills-row.best {
    background: main.$white-200;

    .cell {
      color: main.$color-primary;

      &.label {
        color: main.$black-203;
      }
    }
  }
}

.modal-actions {
  padding: 1.5rem;
  background: main.$white_01;
  display: flex;
  justify-content: center;
  gap: 0.75rem;

  button {
    border-radius: 8px;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    font-weight: main.$font-weight-bold;
    font-family: 'Poppins Bold', Arial, sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    max-width: 180px;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      padding: 1rem 1rem;
      font-size: 0.875rem;
    }
  }

  .cancel-btn {
    background: main.$white;
    color: main.$black-203;
    border: 1px solid main.$grey-402;

    &:hover {
      background: main.$white-200;
    }
  }

  .confirm-btn {
    background: main.$color-primary;
    color: main.$white;
    border: none;

    &:hover {
      background: main.$color-primary_04;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px main.$black-103;
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// Night mode
[theme='night'] {
  .modal-content {
    background: main.$white_01;
  }

  .body {
    background: main.$white_01;
  }

  .summary-note {
    color: main.$black-203_01;
  }

  .fills {
    border-color: main.$grey-400;

    .fills-head,
    .fills-row {
      border-bottom-color: main.$grey-400;

      .cell {
        color: main.$black;

        &.label {
          color: main.$black-203_01;
        }
      }
    }

    .fills-head {
      background: main.$white_07;

      .cell {
        color: main.$black-203_01;
      }
    }

    .fills-row.best {
      background: main.$white_07;

      .cell {
        color: main.$color-primary_06;

        &.label {
          color: main.$black-203_01;
        }
      }
    }
  }

  .modal-actions {
    background: main.$white_01;

    .cancel-btn {
      background: main.$white_01;
      color: main.$black-203_01;
      border-color: main.$grey-400;

      &:hover {
        background: main.$white_07;
      }
    }

    .confirm-btn {
      background: main.$color-primary_04;
      color: main.$black;

      &:hover {
        background: main.$color-primary_07;
        box-shadow: 0 4px 12px main.$black-104;
      }
    }
  }

  .close-btn {
    .day-mode {
      display: none;
    }
    .night-mode {
      display: block;
    }
  }
}

[theme='day'] {
  .close-btn {
    .day-mode {
      display: block;
    }
    .night-mode {
      display: none;
    }
  }
}
</style>
